import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Clear, Light } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  OutlinedInput,
  InputAdornment,
  TablePagination,
  Theme,
  Typography,
  useTheme,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Menu,
} from '@mui/material';
import { IconButton } from '@mui/material';
import {
  GridColDef,
  DataGridPro,
  GridRowParams,
  GridActionsCellItem,
  GridMoreVertIcon,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import * as clientSlice from '../../../data/client.slice';

import PageHeader from '../../controls/PageHeader';
import { ServicePlan } from '../../../data/models/client-program.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import ActivateDialog from '../ActivateDialog';
import { CellActions } from '../../tables/DataGridPro/CellActions';
import ServicePlanDialog from './ServicePlanDialog';

const statusColors = (theme: Theme) => ({
  active: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusActive.main,
  },
  inactive: {
    backgroundColor: theme.palette.statusInactiveBackground.main,
    color: theme.palette.statusInactive.main,
  },
  draft: {
    backgroundColor: theme.palette.statusInactiveBackground.main,
    color: theme.palette.statusDraft.main,
  },
});

const PREFIX = 'ClientServicePlansList';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  grid: `${PREFIX}-grid`,
  statusCellContainer: `${PREFIX}-statusCellContainer`,
  statusCell: `${PREFIX}-statusCell`,
  statusText: `${PREFIX}-statusText`,
  statusDate: `${PREFIX}-statusDate`,
  toolSelect: `${PREFIX}-toolSelect`,
  toolSelectLabel: `${PREFIX}-toolSelectLabel`,
  servicePlanName: `${PREFIX}-servicePlanName`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightTextPrimary.main,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: 327,
    maxWidth: 327,
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: theme.palette.lightActionDisabled60p.main,
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: theme.palette.lightActionDisabled60p.main,
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: '#000',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    [`& .MuiDataGrid-row`]: {
      letterSpacing: '0.17px',
      cursor: 'pointer',
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.statusCellContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  [`& .${classes.statusCell}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 64,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    width: 'fit-content',
    minWidth: 48,

    [`& .${classes.statusText}, & .${classes.statusDate}`]: {
      fontFamily: 'Source Sans Pro',
      fontSize: 12,
      fontStyle: 'normal',
      lineHeight: '100%',
      letterSpacing: 0.17,
    },
    [`& .${classes.statusText}`]: {
      fontWeight: 600,
    },
    [`& .${classes.statusDate}`]: {
      fontWeight: 400,
    },
  },
  [`& .${classes.toolSelect}`]: {
    width: 155,
    color: theme.palette.lightTextPrimary.main,
    fontFamily: 'Source Sans Pro',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolSelectLabel}`]: {
    color: theme.palette.lightActionDisabled60p.main,
    fontFamily: 'Source Sans Pro',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.servicePlanName}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    fontSize: '14px',
    letterSpacing: '0.17px',
    lineHeight: '20px',
  },
}));

const StyledGridActionsCellItem = styled(GridActionsCellItem)(({ theme }) => ({
  '&': {
    color: '#7F7D7F',
    padding: '8px 16px',
    minWidth: '180px',

    '&:hover': {
      color: '#000',
    },
  },
}));

function ActionMenu({
  params,
  actions,
}: {
  params: GridRenderCellParams<ServicePlan>;
  actions: {
    view: () => void;
    edit: () => void;
    duplicate: () => void;
    setInactive: () => void;
    setActive: () => void;
    delete: () => void;
  };
}) {
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <CellActions>
      <IconButton
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }>
        <GridMoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleCloseMenu}>
        {params.row.state === 'active' ? (
          <>
            <StyledGridActionsCellItem
              label={t('client.view')}
              onClick={() => {
                actions.view();
                handleCloseMenu();
              }}
              showInMenu={true}
            />

            <StyledGridActionsCellItem
              label={t('client.duplicate')}
              onClick={() => {
                actions.duplicate();
                handleCloseMenu();
              }}
              showInMenu={true}
            />

            <StyledGridActionsCellItem
              label={t('client.setInactive')}
              onClick={() => {
                actions.setInactive();
                handleCloseMenu();
              }}
              showInMenu={true}
            />
          </>
        ) : (
          <>
            <StyledGridActionsCellItem
              label={t('client.edit')}
              onClick={() => {
                actions.edit();
                handleCloseMenu();
              }}
              showInMenu={true}
            />

            <StyledGridActionsCellItem
              label={t('client.duplicate')}
              onClick={() => {
                actions.duplicate();
                handleCloseMenu();
              }}
              showInMenu={true}
            />

            <StyledGridActionsCellItem
              label={t('client.setActive')}
              onClick={() => {
                actions.setActive();
                handleCloseMenu();
              }}
              showInMenu={true}
            />

            <StyledGridActionsCellItem
              label={t('client.delete')}
              onClick={() => {
                actions.delete();
                handleCloseMenu();
              }}
              showInMenu={true}
            />
          </>
        )}
      </Menu>
    </CellActions>
  );
}

export default function ClientServicePlansList() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const servicePlansList = useAppSelector(clientSlice.selectServicePlanList);
  const loadingServicePlansList = useAppSelector(
    clientSlice.selectLoadingServicePlans,
  );
  const programs = useAppSelector(clientSlice.selectPrograms);
  const client = useAppSelector(clientSlice.selectCurrentClient);
  const theme = useTheme();

  const isSuperReaderUser = useIsSuperReaderUser();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [servicePlanDialogOpen, setServicePlanDialogOpen] =
    React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [selectedServicePlan, setSelectedServicePlan] = React.useState<
    ServicePlan | undefined
  >(undefined);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = React.useState(false);
  const [isDeactivate, setIsDeactivate] = React.useState(false);
  const [isDuplicate, setIsDuplicate] = React.useState(false);

  React.useEffect(() => {
    if (client?.clientBC) {
      dispatch(clientSlice.fetchClientServicePlansAsync(client.clientBC));
    }
  }, [client?.clientBC, dispatch]);

  const filteredServicePlans = useMemo(() => {
    return servicePlansList?.servicePlans.filter((servicePlan) => {
      const matchesSearch = servicePlan.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' || servicePlan.state === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [servicePlansList?.servicePlans, searchQuery, statusFilter]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedServicePlans = useMemo(() => {
    return filteredServicePlans?.slice(startIndex, endIndex);
  }, [filteredServicePlans, startIndex, endIndex]);

  const updateServicePlanEffectiveDates = (
    clientBC: string,
    servicePlanUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      clientSlice.updateServicePlanEffectiveDatesAsync(
        clientBC,
        servicePlanUUID,
        start,
        end,
      ),
    );
  };

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
      setPage(1);
    },
    [setSearchQuery],
  );

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleCreateServicePlan = () => {
    setSelectedServicePlan(undefined);
    setIsDuplicate(false);
    setServicePlanDialogOpen(true);
  };

  const handleServicePlanDialogClose = () => {
    setServicePlanDialogOpen(false);
  };

  const handleClickRow = (params: ServicePlan) => {
    setSelectedServicePlan(params);
    setIsDuplicate(false);
    setServicePlanDialogOpen(true);
  };

  const handleClickEdit = (params: ServicePlan) => {
    setIsDuplicate(false);
    handleClickRow(params);
  };

  const handleClickView = (params: ServicePlan) => {
    setIsDuplicate(false);
    handleClickRow(params);
  };

  const handleClickSetActive = (params: ServicePlan) => {
    setIsDuplicate(false);
    setSelectedServicePlan(params);
    setIsDeactivate(false);
    setActivateDialogOpen(true);
  };

  const handleClickSetInactive = (params: ServicePlan) => {
    setIsDuplicate(false);
    setSelectedServicePlan(params);
    setIsDeactivate(true);
    setActivateDialogOpen(true);
  };

  const handleClickDelete = (params: ServicePlan) => {
    setIsDuplicate(false);
    setSelectedServicePlan(params);
    setConfirmDialogOpen(true);
  };

  const handleClickDuplicate = (params: ServicePlan) => {
    setSelectedServicePlan(params);
    setIsDuplicate(true);
    setServicePlanDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedServicePlan || !client) return;

    dispatch(
      clientSlice.deleteClientServicePlanAsync(
        client.clientBC,
        selectedServicePlan,
      ),
    );
    setConfirmDialogOpen(false);
  };

  const handleConfirmActivate = (start?: string, end?: string) => {
    if (!selectedServicePlan || !client || !selectedServicePlan.servicePlanUUID)
      return;

    updateServicePlanEffectiveDates(
      client.clientBC,
      selectedServicePlan.servicePlanUUID,
      start,
      end,
    );
    setActivateDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('client.name'),
      sortable: false,
      width: 331,
      renderCell: (params) => (
        <Box className={classes.servicePlanName}>
          <span style={{ color: theme.palette.lightActionDisabled90p.main }}>
            {params.row.name}{' '}
          </span>
          <span style={{ color: theme.palette.lightActionDisabled60p.main }}>
            {params.row.description}
          </span>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 295,
      align: 'center',
      sortable: false,
      renderHeader: () => (
        <Typography align="center">{t('client.status')}</Typography>
      ),
      renderCell: (params) => (
        <Box className={classes.statusCellContainer}>
          <Box
            className={classes.statusCell}
            sx={
              statusColors(theme)[params.row.state as keyof typeof statusColors]
            }>
            <Typography className={classes.statusText}>
              {params.row.state.charAt(0).toUpperCase() +
                params.row.state.slice(1)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      flex: 1,
      align: 'right',
      renderCell: (params: GridRenderCellParams<ServicePlan>) => (
        <ActionMenu
          params={params}
          actions={{
            view: () => handleClickView(params.row),
            edit: () => handleClickEdit(params.row),
            duplicate: () => handleClickDuplicate(params.row),
            setInactive: () => handleClickSetInactive(params.row),
            setActive: () => handleClickSetActive(params.row),
            delete: () => handleClickDelete(params.row),
          }}
        />
      ),
    },
  ];

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <PageHeader
          title={`${t('client.servicePlans')}`}
          returnLink={`/clients/${client?.clientBC}`}
          returnLinkText={`${client?.legalName} ${t('client.client')}`}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <OutlinedInput
                size="small"
                className={classes.toolInput}
                placeholder={t('client.search')}
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={
                  <InputAdornment position="start" sx={{ marginLeft: '20px' }}>
                    {searchQuery ? (
                      <IconButton
                        edge="start"
                        onClick={() => {
                          setSearchQuery('');
                          setPage(1);
                        }}>
                        <Clear />
                      </IconButton>
                    ) : (
                      <IconButton edge="start">
                        <Search />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
              <FormControl sx={{ width: 'fit-content' }}>
                <InputLabel
                  variant="standard"
                  htmlFor="status-select"
                  className={classes.toolSelectLabel}>
                  {t('client.status')}
                </InputLabel>
                <Select
                  value={statusFilter}
                  className={classes.toolSelect}
                  onChange={handleStatusFilterChange}>
                  <MenuItem value="all">{t('client.all')}</MenuItem>
                  <MenuItem value="active">{t('client.active')}</MenuItem>
                  <MenuItem value="inactive">{t('client.inactive')}</MenuItem>
                  <MenuItem value="draft">{t('client.draft')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {!isSuperReaderUser && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateServicePlan}>
                {t('client.add')}
              </Button>
            )}
          </Box>
          <DataGridPro
            loading={loadingServicePlansList}
            rows={paginatedServicePlans || []}
            getRowId={(row) => row.servicePlanUUID}
            columns={columns as GridColDef[]}
            sortingMode="server"
            className={`${classes.grid} ${filteredServicePlans?.length === 0 ? 'empty' : ''}`}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnResize={true}
            onRowClick={(params: GridRowParams<ServicePlan>) =>
              handleClickRow(params.row)
            }
          />
          <TablePagination
            component="div"
            count={filteredServicePlans?.length || 0}
            page={page - 1}
            onPageChange={(event, page) => setPage(page + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(1);
            }}
          />
          {!!client && (
            <>
              <ServicePlanDialog
                title={
                  isDuplicate
                    ? t('client.duplicateServicePlan')
                    : !selectedServicePlan
                      ? t('client.addServicePlan')
                      : selectedServicePlan.state === 'active'
                        ? t('client.viewServicePlan')
                        : t('client.editServicePlan')
                }
                client={client}
                programs={programs}
                servicePlan={selectedServicePlan}
                open={servicePlanDialogOpen}
                close={handleServicePlanDialogClose}
              />
              <ConfirmationDialog
                title={t('client.confirmDeleteServicePlanTitle')}
                message={t('client.confirmDeleteServicePlanMessage')}
                open={confirmDialogOpen}
                positiveAction={handleConfirmDelete}
                negativeAction={() => setConfirmDialogOpen(false)}
              />
              <ActivateDialog
                title={
                  isDeactivate
                    ? t('client.confirmDeactivateServicePlanTitle')
                    : t('client.confirmActivateServicePlanTitle')
                }
                message={
                  isDeactivate
                    ? t('client.confirmDeactivateServicePlanMessage')
                    : t('client.confirmActivateServicePlanMessage')
                }
                open={activateDialogOpen}
                forActivate={!isDeactivate}
                handleOk={handleConfirmActivate}
                handleCancel={() => setActivateDialogOpen(false)}
              />
            </>
          )}
        </CardContent>
      </Card>
    </StyledBox>
  );
}
